/**
 * A role-named input picker ("As-built survey", "Design surface", "Boundary",
 * …) — `SourceLayerSelect` extended with an `optional` "None" item (empty id)
 * and grouped options. Plain values in / id out, so it serves both the
 * `useState` dialogs and, through `FormInputRoleField`, the react-hook-form
 * ones. Surface inputs that need per-surface / point-cloud sampling choices
 * keep using `SurfacePicker`, which takes the same role labels.
 */
import { useId, type ReactNode } from 'react';
import { useController, type Control, type FieldPath, type FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useFormMessageText } from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export type InputRole = 'asBuilt' | 'design' | 'terrain' | 'boundary' | 'alignment' | 'ifc';

/** Literal keys (not a template): the typed i18n key union only accepts keys
 *  it can see statically. */
export const INPUT_ROLE_LABEL_KEYS = {
  asBuilt: 'analysisConfig.roles.asBuilt',
  design: 'analysisConfig.roles.design',
  terrain: 'analysisConfig.roles.terrain',
  boundary: 'analysisConfig.roles.boundary',
  alignment: 'analysisConfig.roles.alignment',
  ifc: 'analysisConfig.roles.ifc',
} as const satisfies Record<InputRole, string>;

/** What a REQUIRED role says when there is nothing to pick, unless the caller
 *  passes its own `emptyMessage` — only the as-built survey is a point cloud. */
const EMPTY_MESSAGE_KEYS = {
  asBuilt: 'analysisLauncher.picker.noPointclouds',
  alignment: 'stationVolumes.dialog.noAlignments',
  design: 'analysisLauncher.picker.noLayers',
  terrain: 'analysisLauncher.picker.noLayers',
  boundary: 'analysisLauncher.picker.noLayers',
  ifc: 'analysisLauncher.picker.noLayers',
} as const satisfies Record<InputRole, string>;

export interface InputRoleOption {
  id: string;
  name: string;
}

export interface InputRoleGroup {
  key: string;
  label: ReactNode;
  layers: readonly InputRoleOption[];
}

export interface InputRoleFieldProps {
  role: InputRole;
  /** Overrides the role's own name. */
  label?: ReactNode;
  layers: readonly InputRoleOption[];
  /** When set, rendered as labelled groups instead of `layers`. */
  groups?: readonly InputRoleGroup[];
  /** '' = nothing picked (or "None" when `optional`). */
  value: string;
  onChange: (id: string) => void;
  /** Adds "(optional)" to the label and a "None" item that reports ''. */
  optional?: boolean;
  /** data-testid of the trigger; the empty message gets `${testId}-empty`. */
  testId: string;
  /** id of the trigger; generated when omitted. */
  id?: string;
  help?: ReactNode;
  /** Shown instead of the select when a REQUIRED role has nothing to pick.
   *  Defaults to a per-role message. */
  emptyMessage?: ReactNode;
  disabled?: boolean;
  invalid?: boolean;
  /** A validation message, shown under the field (`${testId}-error`). */
  error?: ReactNode;
  /** Trigger text while nothing is picked; defaults to "Choose a layer…". */
  placeholder?: ReactNode;
}

/** Radix Select treats '' as "no value", so "None" travels as a sentinel. */
const NONE = '__none__';

export function InputRoleField({
  role,
  label,
  layers,
  groups,
  value,
  onChange,
  optional,
  testId,
  id,
  help,
  emptyMessage,
  disabled,
  invalid,
  error,
  placeholder,
}: InputRoleFieldProps) {
  const { t } = useTranslation('project');
  const generatedId = useId();
  const triggerId = id ?? generatedId;
  const optionCount = groups ? groups.reduce((n, g) => n + g.layers.length, 0) : layers.length;
  const item = (l: InputRoleOption) => (
    <SelectItem key={l.id} value={l.id}>
      <span className='min-w-0 flex-1 truncate'>{l.name}</span>
    </SelectItem>
  );

  return (
    <div className='flex min-w-0 flex-col gap-1.5'>
      <Label htmlFor={triggerId}>
        {label ?? t(INPUT_ROLE_LABEL_KEYS[role])}
        {optional && (
          <>
            {' '}
            <span className='font-normal text-slate-500 dark:text-stone-400'>{t('analysisConfig.optional')}</span>
          </>
        )}
      </Label>
      {optionCount === 0 && !optional ? (
        <p data-testid={`${testId}-empty`} className='text-sm text-slate-600 dark:text-stone-400'>
          {emptyMessage ?? t(EMPTY_MESSAGE_KEYS[role])}
        </p>
      ) : (
        <Select
          value={optional && !value ? NONE : value}
          onValueChange={(v) => onChange(v === NONE ? '' : v)}
          disabled={disabled}>
          <SelectTrigger id={triggerId} data-testid={testId} aria-invalid={invalid || undefined} className='w-full min-w-0'>
            <SelectValue placeholder={placeholder ?? t('analysisLauncher.picker.placeholder')} />
          </SelectTrigger>
          <SelectContent>
            {optional && <SelectItem value={NONE}>{t('analysisConfig.none')}</SelectItem>}
            {groups
              ? groups.map((g) => (
                  <SelectGroup key={g.key}>
                    <SelectLabel className='truncate'>{g.label}</SelectLabel>
                    {g.layers.map(item)}
                  </SelectGroup>
                ))
              : layers.map(item)}
          </SelectContent>
        </Select>
      )}
      {help && <p className='text-xs text-slate-600 dark:text-stone-400'>{help}</p>}
      {error && (
        <p data-slot='form-message' data-testid={`${testId}-error`} className='text-destructive text-sm'>
          {error}
        </p>
      )}
    </div>
  );
}

export type FormInputRoleFieldProps<T extends FieldValues> = Omit<InputRoleFieldProps, 'value' | 'onChange'> & {
  control: Control<T>;
  name: FieldPath<T>;
};

/** `InputRoleField` bound to a react-hook-form field holding a layer id. */
export function FormInputRoleField<T extends FieldValues>({ control, name, ...rest }: FormInputRoleFieldProps<T>) {
  const { field, fieldState } = useController({ control, name });
  // A form that raises message tags maps them to text once (`FormMessageText`).
  const format = useFormMessageText();
  const message = fieldState.error?.message;
  return (
    <InputRoleField
      {...rest}
      value={(field.value as string | undefined) ?? ''}
      onChange={field.onChange}
      invalid={rest.invalid || !!fieldState.error}
      // The raw message, exactly as `FormMessage` prints it — callers' schemas
      // already put translated text there (e.g. `referenceLayerRequired`).
      error={rest.error ?? (message && format ? format(message) : message)}
    />
  );
}
